// URL view-state adapters (EPIC #157, Lane E1 — design §3.1).
//
// The two thin, impure adapters that keep the browser URL and the run-store's
// view slice in sync — the only code in `lib/view/` allowed to touch `window` /
// history. All the logic they lean on is the pure codec in `urlState.ts`, and
// history/window/store are injected, so these unit-test in node with fakes.
//
//   state → URL:  a store subscription serializes the view slice. Switching the
//                 focused forecast is a discrete transition → pushState (Back/
//                 Forward navigate forecasts); a compare-only change (or a
//                 forecastId being stamped onto the already-active run) keeps the
//                 same run focused → replaceState.
//   URL → state:  a `popstate` handler re-applies the URL's view. It dispatches
//                 ONLY the view actions from `viewStateActions` (activeSet /
//                 comparisonSet) — it imports no domain command — so Back can
//                 never cancel or mutate a running forecast (structural guarantee).
import {
  parseViewParams,
  readEntrySourceParam,
  selectViewState,
  serializeViewParams,
  stripEntrySourceParam,
  viewStateActions,
} from "./urlState.js";
import type { ViewState } from "./urlState.js";
import { selectRun } from "../forecast/selectors.js";
import type { RunAction, RunState } from "../forecast/reducer.js";

/** The slice of the run-store this module needs — kept structural so the real
 *  `runStore` (and a test fake) both satisfy it without importing the store. */
interface ViewSyncStore {
  getState: () => RunState;
  subscribe: (listener: (state: RunState, prevState: RunState) => void) => () => void;
  dispatch: (action: RunAction) => unknown;
}

/** The history / location / window surface these adapters actually touch —
 *  structural (not the full DOM types) so the real objects AND the minimal test
 *  fakes both satisfy them without `as unknown as History` casts. */
type HistoryLike = Pick<History, "pushState" | "replaceState">;
type LocationLike = Pick<Location, "pathname" | "search" | "hash">;
interface ViewWindow {
  history: HistoryLike;
  location: LocationLike;
  addEventListener: (type: "popstate", listener: () => void) => void;
  removeEventListener: (type: "popstate", listener: () => void) => void;
}

/** True when two id lists are element-wise equal (order-sensitive). */
function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/**
 * Write `view` into the URL via the history API, choosing push vs replace by the
 * caller-supplied `mode`. No-ops when the serialized view already matches the
 * current URL — which is what makes the popstate round-trip terminate (the URL is
 * already where popstate navigated, so re-applying it writes nothing).
 */
export function writeViewToUrl(
  view: ViewState,
  {
    history,
    location,
    mode,
  }: { history: HistoryLike; location: LocationLike; mode: "push" | "replace" },
): void {
  const nextSearch = serializeViewParams(view, location.search);
  if (nextSearch === location.search) return;
  const url = `${location.pathname}${nextSearch}${location.hash}`;
  if (mode === "push") history.pushState(null, "", url);
  else history.replaceState(null, "", url);
}

/** Read the current URL's view (impure sibling of the pure `parseViewParams`),
 *  so callers outside `lib/view/` never touch `location.search` directly. */
export function readViewFromLocation(
  win: { location: Pick<Location, "search"> } = window,
): ViewState {
  return parseViewParams(win.location.search);
}

/**
 * Wire the two adapters to a store + window and return a teardown. Call ONCE, on
 * mount, AFTER boot hydration has applied the URL to the store — so the initial
 * runs-loaded burst can't clobber inbound params before they're read. Captures
 * the current view as the baseline and only writes on genuine view changes.
 */
export function installUrlSync({
  store,
  window: win,
}: {
  store: ViewSyncStore;
  window: ViewWindow;
}): () => void {
  let prev = selectViewState(store.getState());
  // Set while we re-apply a popstate view: applying it is >1 dispatch, and each
  // would otherwise re-enter the subscription and rewrite (even push) the URL
  // mid-apply. The guard makes URL→state one-directional for the duration.
  let applying = false;

  const unsubscribe = store.subscribe((state, prevState) => {
    if (applying) return;
    const next = selectViewState(state);
    if (next.activeId === prev.activeId && sameIds(next.compareIds, prev.compareIds)) return;
    // A changed underlying run id is real navigation (push); anything else that
    // moved the serialized view — a compare toggle, a forecastId stamped onto
    // the same focused run, or the focused run being removed (RUN_REMOVED clears
    // the selection with it; nothing was navigated to) — replaces.
    const focusedRunRemoved =
      prevState.activeRunId != null &&
      state.activeRunId == null &&
      !selectRun(state, prevState.activeRunId);
    const mode =
      state.activeRunId !== prevState.activeRunId && !focusedRunRemoved ? "push" : "replace";
    writeViewToUrl(next, { history: win.history, location: win.location, mode });
    prev = next;
  });

  const onPopState = () => {
    applying = true;
    try {
      const view = parseViewParams(win.location.search);
      for (const action of viewStateActions(view, store.getState())) store.dispatch(action);
    } finally {
      applying = false;
    }
    prev = selectViewState(store.getState());
  };
  win.addEventListener("popstate", onPopState);

  return () => {
    unsubscribe();
    win.removeEventListener("popstate", onPopState);
  };
}

/**
 * Read the URL's declared entry source (`?src=alert`) and strip it from the address bar
 * (replaceState, nothing else in the URL changes), so it is consumed exactly once per page
 * load. Returns the source or null; never throws.
 */
export function consumeEntrySource(
  win: { history: HistoryLike; location: LocationLike } = window,
): string | null {
  try {
    const { pathname, search, hash } = win.location;
    const src = readEntrySourceParam(search);
    const stripped = stripEntrySourceParam(search);
    if (stripped !== search) win.history.replaceState(null, "", `${pathname}${stripped}${hash}`);
    return src;
  } catch {
    return null;
  }
}
